/* eslint-disable prefer-arrow-callback */
import { type MutableRefObject, type RefObject, useCallback, useRef } from "react";

import { SCENARIO_SCROLL } from "consts";

import { type ScenarioBuilderHandle, type ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { isScenarioComplete } from "pages/app/scenario-analysis/utils/scenarioValidation";
import { getContainerTop, getScrollParent, isAlignedTo, scrollToElement } from "pages/app/scenario-analysis/utils/scrollUtils";

type ScrollToBuilderOptions = {
  behavior?: ScrollBehavior;
  // When true, re-checks alignment on the next frame and scrolls again if the first attempt drifted.
  correct?: boolean;
};

type UseScenarioScrollArgs = {
  scenarios: ScenarioData[];
  scenarioBuilderRef: RefObject<ScenarioBuilderHandle>;
  // True while the page is on the project a field import landed on; cleared when the user leaves it.
  fieldImportAlreadyPersistedRef: MutableRefObject<boolean>;
  showTransitionModal: boolean;
};

export type ScenarioScroll = ReturnType<typeof useScenarioScroll>;

// Owns the scenario page's scroll DOM targets and all scroll mechanics (offsets, scroll-parent
// detection, rAF retry, drift re-anchor). Callers decide WHEN to scroll; this hook owns HOW.
export const useScenarioScroll = ({ scenarios, scenarioBuilderRef, fieldImportAlreadyPersistedRef, showTransitionModal }: UseScenarioScrollArgs) => {
  const builderRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const economicsRef = useRef<HTMLDivElement>(null);
  const lastReanchorAtRef = useRef(0);

  const scrollToBuilder = useCallback(function scrollToBuilder({ behavior = "auto", correct = false }: ScrollToBuilderOptions = {}) {
    if (!builderRef.current) return;
    scrollToElement(builderRef.current, SCENARIO_SCROLL.BUILDER_OFFSET, behavior);
    if (!correct) return;
    requestAnimationFrame(function correctBuilderAlignment() {
      const builder = builderRef.current;
      if (builder && !isAlignedTo(builder, SCENARIO_SCROLL.BUILDER_OFFSET)) scrollToElement(builder, SCENARIO_SCROLL.BUILDER_OFFSET, behavior);
    });
  }, []);

  const scrollToChart = useCallback(function scrollToChart(behavior: ScrollBehavior = "smooth") {
    if (chartRef.current) scrollToElement(chartRef.current, SCENARIO_SCROLL.CHART_OFFSET, behavior);
  }, []);

  // The economics section sits below the same sticky bar as the chart, so it uses the same clearance.
  const scrollToEconomics = useCallback(function scrollToEconomics(behavior: ScrollBehavior = "smooth") {
    if (economicsRef.current) scrollToElement(economicsRef.current, SCENARIO_SCROLL.CHART_OFFSET, behavior);
  }, []);

  // Enough of the chart to read, not merely one edge of it, so a view swap below the fold still scrolls.
  const isChartInView = useCallback(function isChartInView() {
    const chart = chartRef.current;
    if (!chart) return false;

    const container = getScrollParent(chart.parentElement);
    const viewportTop = getContainerTop(container);
    const viewportBottom = container === document.documentElement ? window.innerHeight : viewportTop + container.clientHeight;

    const { top, bottom } = chart.getBoundingClientRect();
    const visible = Math.min(bottom, viewportBottom) - Math.max(top, viewportTop);
    return visible >= Math.min(chart.offsetHeight, viewportBottom - viewportTop) * SCENARIO_SCROLL.CHART_IN_VIEW_RATIO;
  }, []);

  // Two frames to let the card expand/mount, then a third to re-check and correct if still settling.
  const scrollToScenarioCard = useCallback(
    function scrollToScenarioCard(scenarioId: string) {
      requestAnimationFrame(function waitForCardLayout() {
        requestAnimationFrame(function applyCardScroll() {
          const card = scenarioBuilderRef.current?.getCardElement?.(scenarioId);
          if (!card) return;
          scrollToElement(card, SCENARIO_SCROLL.INCOMPLETE_SCENARIO_OFFSET);
          requestAnimationFrame(function correctCardAlignment() {
            const settledCard = scenarioBuilderRef.current?.getCardElement?.(scenarioId);
            if (settledCard && !isAlignedTo(settledCard, SCENARIO_SCROLL.INCOMPLETE_SCENARIO_OFFSET)) {
              scrollToElement(settledCard, SCENARIO_SCROLL.INCOMPLETE_SCENARIO_OFFSET);
            }
          });
        });
      });
    },
    [scenarioBuilderRef]
  );

  const scrollToFirstIncompleteScenario = useCallback(
    function scrollToFirstIncompleteScenario(): boolean {
      const firstIncomplete = scenarios.find((scenario) => !isScenarioComplete(scenario));
      if (!firstIncomplete) return false;
      if (!scenarioBuilderRef.current?.getCardElement?.(firstIncomplete.id)) return false;
      scrollToScenarioCard(firstIncomplete.id);
      return true;
    },
    [scenarios, scenarioBuilderRef, scrollToScenarioCard]
  );

  // Field-import landing: retry the builder scroll across frames until it lands at the expected
  // offset (or the attempt cap is hit). Returns a cleanup that cancels any pending frame.
  const scheduleBuilderScrollForImportLanding = useCallback(
    function scheduleBuilderScrollForImportLanding() {
      let attempts = 0;
      let frameId = 0;
      let cancelled = false;

      function tryScroll() {
        if (cancelled || attempts >= SCENARIO_SCROLL.BUILDER_SCROLL_RETRY_LIMIT) return;
        attempts += 1;

        if (!builderRef.current) {
          frameId = requestAnimationFrame(tryScroll);
          return;
        }

        scrollToBuilder({ behavior: "instant" });
        frameId = requestAnimationFrame(function retryUntilAligned() {
          if (cancelled || isAlignedTo(builderRef.current, SCENARIO_SCROLL.BUILDER_OFFSET)) return;
          tryScroll();
        });
      }

      // Wait two frames so the modal's initial layout commits before we measure.
      frameId = requestAnimationFrame(function waitForLayoutToCommit() {
        frameId = requestAnimationFrame(tryScroll);
      });

      return function cancelScheduledBuilderScroll() {
        cancelled = true;
        cancelAnimationFrame(frameId);
      };
    },
    [scrollToBuilder]
  );

  // Re-anchors the builder if layout reflow or browser scroll restoration has drifted us away from
  // it (or pushed the chart above the builder) while the field-import transition modal is open.
  const reanchorBuilderIfDrifted = useCallback(
    function reanchorBuilderIfDrifted() {
      if (!fieldImportAlreadyPersistedRef.current || !showTransitionModal) return;
      const builder = builderRef.current;
      if (!builder) return;

      const builderTop = builder.getBoundingClientRect().top;
      const chartTop = chartRef.current?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;
      const chartIsAboveBuilder = chartTop < builderTop;
      if (isAlignedTo(builder, SCENARIO_SCROLL.BUILDER_OFFSET) && !chartIsAboveBuilder) return;

      const now = Date.now();
      if (now - lastReanchorAtRef.current < SCENARIO_SCROLL.BUILDER_REANCHOR_THROTTLE_MS) return;
      lastReanchorAtRef.current = now;

      scrollToBuilder({ behavior: "instant" });
    },
    [showTransitionModal, scrollToBuilder, fieldImportAlreadyPersistedRef]
  );

  return {
    builderRef,
    chartRef,
    economicsRef,
    scrollToBuilder,
    scrollToChart,
    scrollToEconomics,
    isChartInView,
    scrollToScenarioCard,
    scrollToFirstIncompleteScenario,
    scheduleBuilderScrollForImportLanding,
    reanchorBuilderIfDrifted,
  };
};
